import type { Dispatch, UnknownAction } from '@reduxjs/toolkit'
import { useMemo, type ComponentType } from 'react'
import { useSelector, useStore } from 'react-redux'

import { createWindowNavigation, isWorkingDescriptor, type WindowEntry } from './navigation'
import { ROOT_PATH, windowTitle, type WindowRegistry } from './registry'
import type { WindowMatch, WindowParams, WindowSize, WindowSourceMark } from './types'
import { returnForgotten, tabClosed, type WindowsState, type WindowTab } from './windowsSlice'

/** Предел кэша видов окон: при превышении кэш очищается целиком. */
const VIEW_CACHE_LIMIT = 200

/**
 * Окно глазами оболочки: всё, что нужно шапке, полосе вкладок и рамке, без
 * RootState. React-часть runtime работает только с этим видом, поэтому
 * компоненты оболочки не зависят от формы приложения.
 */
export interface WindowView {
  kind: string
  identity: string
  params: WindowParams
  label: string
  title: string
  size: WindowSize | undefined
  /** Чьё окно: источник и оттенок рамки; `null` — окно без источника. */
  source: WindowSourceMark | null
  resourceId: string | null
  /** Раздел меню окна (UI-SECTIONS); `null` — окно вне разделов. */
  section: string | null
  /**
   * Рабочее окно (UI-RMK-FLOW, раздел 1): у него есть пункт меню или вкладка раздела.
   * Оно показывается только на своём адресе — поверх экрана открываются диалоги и инструменты.
   */
  working: boolean
  /** Открывается на своём адресе, а не поверх экрана: рабочее окно или страница раздела (`presentation: 'page'`). */
  onAddress: boolean
}

export interface MenuItem {
  kind: string
  label: string
  path: string
  /** Смысловое имя значка (`WindowMenuPlace.icon`); `null` — общий значок. */
  icon: string | null
  /** Раздел, вход которого этот пункт; `null` — пункт-окно вне разделов. */
  section: string | null
  /** Право, без которого пункт не показывается (`WindowMenuPlace.requires`); `null` — виден всем. */
  requires: string | null
}

/** Окно-вкладка раздела: какое окно, для ресурсов какого вида и права. */
export interface SectionTabView {
  kind: string
  label: string
  resourceKind: string
  capability: string
}

/**
 * Раздел меню глазами оболочки (UI-SECTIONS): вход и окна, которые дают
 * вкладки. Какие ресурсы станут вкладками, оболочка узнаёт у сервера («мои
 * ресурсы» с правом окна) — вид ресурса здесь только данные, не ветвление.
 */
export interface SectionView {
  id: string
  /** Подпись раздела в меню и в шапке: подпись окна-входа. */
  label: string
  /** Смысловое имя значка раздела (`menu.icon` входа); `null` — общий значок. */
  icon: string | null
  path: string
  homeKind: string
  /** Подпись собственной вкладки входа; `null` — вход только выбирает вкладку ресурса. */
  homeTab: string | null
  /** Вход раздела — плитки источников; выбранный источник показывается под полосой «Источник · Сменить». */
  pickResource: boolean
  tabs: readonly SectionTabView[]
  /** Собственные вкладки раздела без ресурса («Настройка»): окно и подпись, по порядку. */
  ownTabs: readonly SectionOwnTabView[]
}

export interface SectionOwnTabView {
  kind: string
  label: string
}

/** Команды навигации окон, уже привязанные к стору. */
export interface WindowCommands {
  enter(entry: WindowEntry): boolean
  close(location: string): string
  switchToTab(id: string, current: string): string | null
  createTab(location: string): boolean
  closeTab(id: string): void
  forgetReturn(identity: string): void
}

/**
 * Связка реестра, навигации и binding-компонентов окон. Создаётся composition
 * один раз; React-компоненты runtime получают её через `WindowShellProvider`.
 */
export interface WindowShell {
  match(pathname: string): WindowView | undefined
  parseRef(ref: string): WindowView | undefined
  refOf(kind: string, params?: WindowParams): string
  pathOf(kind: string, params?: WindowParams): string
  menu(group: 'work' | 'settings'): readonly MenuItem[]
  /** Раздел по имени; `undefined` — такого раздела нет. */
  section(id: string): SectionView | undefined
  /** Все адреса окон — для маршрутизатора. */
  routes(): readonly { kind: string; path: string }[]
  /** Содержимое окна: binding приложения. Пакет экран не регистрирует сам. */
  renderer(kind: string): ComponentType | undefined
  useTabs(): readonly WindowTab[]
  useReturn(identity: string | undefined): string | undefined
  useCommands(): WindowCommands
}

function viewOf<R>(match: WindowMatch<R>): WindowView {
  return {
    kind: match.descriptor.kind,
    identity: match.identity,
    params: match.params,
    label: match.descriptor.label,
    title: windowTitle(match),
    size: match.descriptor.size,
    source: match.descriptor.source ?? null,
    resourceId: match.params.rid ?? null,
    section: match.descriptor.section?.id ?? null,
    working: isWorkingDescriptor(match.descriptor),
    onAddress: isWorkingDescriptor(match.descriptor) || match.descriptor.presentation === 'page',
  }
}

export interface WindowShellOptions<R> {
  registry: WindowRegistry<R>
  selectWindows: (root: R) => WindowsState
  /** kind → binding-компонент. Окно без binding — ошибка сборки приложения. */
  renderers: Readonly<Record<string, ComponentType>>
}

export function createWindowShell<R>({ registry, selectWindows, renderers }: WindowShellOptions<R>): WindowShell {
  for (const descriptor of registry.all) {
    if (renderers[descriptor.kind] === undefined) {
      throw new Error(`Окно «${descriptor.kind}» объявлено, но binding для него не зарегистрирован`)
    }
  }
  for (const kind of Object.keys(renderers)) {
    if (registry.ofKind(kind) === undefined) throw new Error(`Binding «${kind}» есть, окна с таким именем нет`)
  }
  // Раздел, в который пакет вида принёс окно, обязан иметь вход: иначе вкладки некуда показать (UI-SECTIONS).
  for (const descriptor of registry.all) {
    const id = descriptor.section?.id
    if (id !== undefined && registry.section(id) === undefined) {
      throw new Error(`Окно «${descriptor.kind}» ссылается на раздел «${id}», у которого нет входа`)
    }
  }

  const navigation = createWindowNavigation(registry, selectWindows)
  /**
   * Один и тот же адрес — один и тот же объект вида: иначе `params` и
   * обработчики окна пересоздавались бы на каждом рендере, эффекты binding-ов
   * срабатывали бы зря, а модальное окно забирало бы фокус у поля ввода.
   */
  const cache = new Map<string, WindowView | undefined>()
  /** Разделы неизменны после сборки: один объект на раздел — стабильные пропсы и зависимости эффектов. */
  const sections = new Map<string, SectionView | undefined>()
  const sectionView = (id: string): SectionView | undefined => {
    if (sections.has(id)) return sections.get(id)
    const section = registry.section(id)
    const view =
      section === undefined
        ? undefined
        : {
            id,
            label: section.home.label,
            icon: section.home.menu?.icon ?? null,
            path: section.home.paths[0] ?? ROOT_PATH,
            homeKind: section.home.kind,
            homeTab: section.homeTab,
            pickResource: section.pickResource,
            ownTabs: section.ownTabs.flatMap((descriptor) =>
              descriptor.section?.ownTab === undefined ? [] : [{ kind: descriptor.kind, label: descriptor.section.ownTab.label }],
            ),
            tabs: section.tabs.flatMap((descriptor) =>
              descriptor.section?.tab === undefined
                ? []
                : [
                    {
                      kind: descriptor.kind,
                      label: descriptor.section.tab.label,
                      resourceKind: descriptor.section.tab.resourceKind,
                      capability: descriptor.section.tab.capability,
                    },
                  ],
            ),
          }
    sections.set(id, view)
    return view
  }
  const cached = (key: string, compute: () => WindowMatch<R> | undefined): WindowView | undefined => {
    if (cache.has(key)) return cache.get(key)
    if (cache.size > VIEW_CACHE_LIMIT) cache.clear()
    const match = compute()
    const view = match === undefined ? undefined : viewOf(match)
    cache.set(key, view)
    return view
  }

  return {
    match: (pathname) => cached(`path:${pathname}`, () => registry.match(pathname)),
    parseRef: (ref) => cached(`ref:${ref}`, () => registry.parseRef(ref)),
    refOf: (kind, params) => registry.refOf(kind, params),
    pathOf: (kind, params) => registry.pathOf(kind, params),
    menu: (group) =>
      registry.menu(group).map((descriptor) => ({
        kind: descriptor.kind,
        label: descriptor.label,
        path: descriptor.paths[0] ?? ROOT_PATH,
        icon: descriptor.menu?.icon ?? null,
        section: descriptor.section?.home !== undefined ? descriptor.section.id : null,
        requires: descriptor.menu?.requires ?? null,
      })),
    section: sectionView,
    routes: () =>
      registry.all.flatMap((descriptor) => descriptor.paths.map((path) => ({ kind: descriptor.kind, path }))),
    renderer: (kind) => renderers[kind],
    useTabs: () => useSelector((root: R) => selectWindows(root).tabs),
    useReturn: (identity) =>
      useSelector((root: R) => (identity === undefined ? undefined : selectWindows(root).returns[identity]?.from)),
    useCommands: () => {
      const store = useStore<R>()
      return useMemo<WindowCommands>(() => {
        const dispatch: Dispatch<UnknownAction> = (action) => store.dispatch(action)
        const getState = () => store.getState()
        return {
          enter: (entry) => navigation.enterWindow(entry)(dispatch, getState),
          close: (location) => navigation.closeWindow(location)(dispatch, getState),
          switchToTab: (id, current) => navigation.switchToTab(id, current)(dispatch, getState),
          createTab: (location) => navigation.createTab(location)(dispatch, getState),
          closeTab: (id) => {
            store.dispatch(tabClosed(id))
          },
          forgetReturn: (identity) => {
            store.dispatch(returnForgotten(identity))
          },
        }
      }, [store])
    },
  }
}
